export const NODE_TYPES = [
  'trigger',
  'condition',
  'switch',
  'lookup',
  'action',
];

export const emptyGraph = () => ({
  schema_version: 1,
  nodes: [],
  edges: [],
});

export const defaultNodeData = type => {
  switch (type) {
    case 'trigger':
      return { event: '', conditions: [] };
    case 'condition':
      return { conditions: [] };
    case 'switch':
      return { attribute_key: '', cases: [], has_default: true };
    case 'lookup':
      return {
        attribute_key: '',
        action_name: '',
        rows: [],
        default_params: [],
        on_error: 'continue',
      };
    case 'action':
      return { action_name: '', action_params: [], on_error: 'continue' };
    default:
      return {};
  }
};

export const nextNodeId = nodes => {
  const max = nodes.reduce((acc, node) => {
    const match = /^n(\d+)$/.exec(node.id);
    return match ? Math.max(acc, Number(match[1])) : acc;
  }, 0);
  return `n${max + 1}`;
};

export const nextCaseId = cases => {
  const max = (cases || []).reduce((acc, switchCase) => {
    const match = /^c(\d+)$/.exec(switchCase.id);
    return match ? Math.max(acc, Number(match[1])) : acc;
  }, 0);
  return `c${max + 1}`;
};

// Handles a node exposes for outgoing edges; mirrors the backend contract
// (Workflows::GraphValidator / Runner).
export const sourceHandles = node => {
  if (node.type === 'condition') return ['true', 'false'];
  if (node.type === 'switch') {
    const handles = (node.data.cases || []).map(switchCase => switchCase.id);
    if (node.data.has_default) handles.push('default');
    return handles;
  }
  return ['default'];
};

// vue-flow nodes/edges -> persisted graph shape (strips runtime-only keys).
export const serializeGraph = (nodes, edges) => ({
  schema_version: 1,
  nodes: nodes.map(node => ({
    id: node.id,
    type: node.type,
    position: { x: node.position.x, y: node.position.y },
    data: node.data,
  })),
  edges: edges.map(edge => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    ...(edge.sourceHandle ? { sourceHandle: edge.sourceHandle } : {}),
  })),
});

// persisted graph -> vue-flow nodes/edges.
export const deserializeGraph = graph => ({
  nodes: (graph?.nodes || []).map(node => ({
    id: node.id,
    type: node.type,
    position: node.position || { x: 0, y: 0 },
    data: node.data || defaultNodeData(node.type),
  })),
  edges: (graph?.edges || []).map(edge => ({
    id:
      edge.id ||
      `e-${edge.source}-${edge.sourceHandle || 'default'}-${edge.target}`,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle || null,
  })),
});

// Client-side hints mirroring backend publish validation; keys are i18n-able codes.
export const lintGraph = (nodes, edges) => {
  const issues = [];
  const triggers = nodes.filter(node => node.type === 'trigger');
  if (triggers.length !== 1) issues.push('TRIGGER_COUNT');
  if (triggers.length === 1 && !triggers[0].data.event) {
    issues.push('TRIGGER_EVENT_MISSING');
  }
  nodes.forEach(node => {
    if (node.type === 'action' && !node.data.action_name) {
      issues.push('ACTION_NAME_MISSING');
    }
    if (node.type === 'switch') {
      if (!node.data.attribute_key) issues.push('SWITCH_ATTRIBUTE_MISSING');
      if (!(node.data.cases || []).length) issues.push('SWITCH_CASES_MISSING');
    }
    if (node.type === 'lookup') {
      if (!node.data.attribute_key) issues.push('LOOKUP_ATTRIBUTE_MISSING');
      if (!node.data.action_name) issues.push('LOOKUP_ACTION_MISSING');
      if (!(node.data.rows || []).length) issues.push('LOOKUP_ROWS_MISSING');
    }
  });
  const connected = new Set(edges.flatMap(edge => [edge.source, edge.target]));
  if (nodes.length > 1 && nodes.some(node => !connected.has(node.id))) {
    issues.push('DISCONNECTED_NODES');
  }
  return [...new Set(issues)];
};
